// eslint-disable-next-line no-unused-vars
import React from 'react';
import { bool, func, object } from 'prop-types';
import { Button, Loading } from '@carbon/react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Package, Trash } from 'lucide-react';

import Img from 'Containers/Image/Image';
import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import Badges from 'Components/Badges/Badges';

const StyledProductOfferItem = styled.div`
  align-items: center;
  display: flex;
  min-height: 86px;
  padding: 12px 0;
  position: relative;
  width: 100%;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };
  }

  > .cds--loading-overlay {
    position: absolute;
    z-index: 2;
  }

  .preview {
    align-items: center;
    border: 1px solid ${ props => props.theme.colors.shadowGray };
    display: flex;
    flex: 0 0 54px;
    height: 54px;
    justify-content: center;
    margin-right: 10px;
    overflow: hidden;
    padding: 3px;

    .image {
      max-height: 100%;
      max-width: 100%;
    }
  }

  .details {
    flex: 1;
    min-width: 0;

    .badges {
      justify-content: flex-start;
      margin: 2px 0 0;

      .badge {
        font-size: 12px;
        min-height: auto;
      }
    }
  }

  .title {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    color: ${ props => props.theme.colors.darkGray };
    display: -webkit-box;
    font-size: 0.875rem;
    line-height: 1.25rem;
    overflow: hidden;

    &:hover {
      color: ${ props => props.theme.colors.blue };
    }
  }

  .sku {
    color: ${ props => props.theme.colors.darkGray };
    font-size: 0.875rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;

    span {
      color: ${ props => props.theme.colors.gray };
      font-weight: normal;
    }
  }

  .quantitySelector {
    flex: 0 0 auto;
    margin-left: 12px;

    input {
      font-size: 0.875rem;
    }
  }

  .delete {
    background: #fef2e8;
    border: 1px solid #dde6f2;
    color: #3a3a3a;
    flex: 0 0 36px;
    height: 36px;
    margin-left: 12px;
    min-height: 36px;
    min-width: 36px;
    padding: 0;

    &:hover {
      background: #fbb2ac;
    }

    svg {
      margin: auto;
    }
  }
`;

const ProductOfferItem = ({ product, isSavedOffer, loading, disabled, onDelete }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const productId = product.productId || product.id;
  const productUrl = `/products/${ product.slug || 'slug' }/pd/${ productId }`;

  return (
    <StyledProductOfferItem theme={ theme }>
      { loading && <Loading active small withOverlay /> }
      <Link className="preview" to={ productUrl }>
        <Img
          className="image"
          src={ product.pictureUrl }
          unloader={ <Package size={ 28 } strokeWidth={ 1.25 } /> }
          alt={ product.title || product.name || '' }
        />
      </Link>
      <div className="details">
        <Link className="title" to={ productUrl }>
          { product.title || product.name }
        </Link>
        { (product.code || product.erpProductId) && (
          <p className="sku">
            { t('product.code') }: <span>{ product.code || product.erpProductId }</span>
          </p>
        ) }
        { (isSavedOffer ? !!product?.supplierId : !product?.hasPrice) && (
          <Badges isSupplierOffer />
        ) }
      </div>
      <QuantitySelect
        value={ Number(product.quantity ?? 1) }
        disabled
        onChange={ () => {} }
      />
      <Button
        className="delete"
        kind="ghost"
        type="button"
        disabled={ loading || disabled }
        iconDescription={ t('common.delete') }
        onClick={ onDelete }
      >
        <Trash size={ 18 } />
      </Button>
    </StyledProductOfferItem>
  );
};

ProductOfferItem.propTypes = {
  product: object,
  isSavedOffer: bool,
  loading: bool,
  disabled: bool,
  onDelete: func
};

export default ProductOfferItem;
